/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Enterprise is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Enterprise is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Enterprise, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Enterprise:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Follows a 'code splitting' architecture.
 *  - Is completely custom built (no JS libraries other than instant.page)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Enterprise broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
 *  4.  on:line-item:change
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - product: the product object (includes a list of all variants)
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart. To listen for this, see the
 * on:line-item:change event.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the variant was added
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:line-item:change
 * -------------------------------------------------------------------------------------------------
 * Fires when the quantity of an item already in the cart is updated. Note, if the 'newQuantity' is
 * 0, this indicates the item was removed from that cart.
 *
 * Note, when adding a variant to the cart - this event will fire if that variant is already in the
 * cart (i.e. the quantity is incremented). In this situation, 'on:cart:add' will not fire.
 *
 * How to listen:
 * document.addEventListener('on:line-item:change', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the quantity change was completed
 *   - variantId: id of the variant that was just updated
 *   - newQuantity: new quantity of the line item
 *   - oldQuantity: old quantity of the line item
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes. See the 'Media Queries' section in this file
 * for more.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Enterprise utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 * =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Enterprise only has one third-party dependency: instant.page (https://instant.page/).
 *
 * It's included locally and is only active if it has been enabled in
 * 'Theme Settings > Advanced > Preload links on hover'.
 *
 * Instant.page is a JavaScript library that speeds up page loads by preloading links as soon as the
 * customer hovers over them.
 *
 *
 *
 * =================================================================================================
 * Code Splitting
 * =================================================================================================
 * We followed the ‘code splitting’ technique when building Enterprise.
 *
 * Code splitting consists in writing JavaScript (and CSS)in a modularized way within typically
 * small, more manageable files that can be loaded on-demand, as needed. The idea is to improve the
 * performance of our theme by reducing the amount of code that needs to be loaded upfront.
 *
 * If the customer is visiting a specific page of the theme that requires certain JavaScript
 * functionality, only the code needed for that page will be loaded, rather than one large
 * JavaScript file containing largely unused code. For example, the file media-gallery.js will
 * only be loaded if there is a media gallery on the page.
 *
 * Shopify uses HTTP/2, which is the newer version of the HTTP protocol used to deliver web content.
 * HTTP/2 supports multiplexing, which means that multiple requests can be sent over a single
 * connection at the same time - meaning multiple JS files are essentially served at the speed of a
 * single file.
 *
 * The only JS file which is served on every page in Enterprise is 'main.js'. Main.js contains
 * utility JS which is likely to be needed by many scripts. This is outlined more in the next
 * section.
 *
 *
 *
 * =================================================================================================
 * Utilities
 * =================================================================================================
 * Enterprise provides a few utility utilities, contained in main.js. Some of the key ones are
 * outlined below. See main.js for more.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 1) Lazy Loading
 * -------------------------------------------------------------------------------------------------
 * Lazy loading is a technique for delaying the loading of certain elements until they are needed,
 * which can help improve page load times.
 *
 * We use three functions used for lazy loading images and scripts in our theme:
 *
 *  - setImageSources function - copies the data-src and data-srcset attributes of lazy loaded
 *    images to their src and srcset attributes.
 *  - initLazyImages function - uses the IntersectionObserver API to lazy load images when needed.
 *  - initLazyScript function - only loads a script when a specific element is scrolled into view.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) Cookies
 * -------------------------------------------------------------------------------------------------
 * Cookies are small pieces of data that can be stored on a user's computer. They can be useful for
 * tracking user activity, remembering user preferences or other similar purposes.
 *
 * We use three functions to manage cookies:
 *
 *  - setCookie function - sets a cookie with a given name, value and number of days until it
 *  should expire.
 *  - getCookie function - takes in the name of a cookie to retrieve its value.
 *  - deleteCookie function - takes in the name of a cookie to delete it.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) Media Queries
 * -------------------------------------------------------------------------------------------------
 * The theme creates a theme.mediaMatches object (in theme.liquid) for several key screen sizes
 * specified in our theme, and adds listeners for each media query.
 *
 * These are:
 *
 * mediaQueries: {
 *  sm: '(min-width: 600px)',
 *  md: '(min-width: 769px)',
 *  lg: '(min-width: 1024px)',
 *  xl: '(min-width: 1280px)',
 *  xxl: '(min-width: 1536px)',
 *  portrait: '(orientation: portrait)'
 * }
 *
 * If a breakpoint is crossed, the mediaMatches values are updated and an 'on:breakpoint-change'
 * event is dispatched.
 *
 * You can request the entire theme.mediaMatches object to check which media queries are currently
 * matched. In this case, the returned data will be an object with the names of the media queries as
 * keys, and boolean values indicating whether they are currently matched or not.
 *
 * Example:
 *
 * {
 *  sm: true,
 *  md: true,
 *  lg: true,
 *  xl: true,
 *  xxl: false,
 *  portrait: false
 * }
 *
 * You can reference a specific media query to check if it's currently matched by using:
 * theme.mediaMatches.lg.
 *
 * To check if you're on mobile you can use:
 * !theme.mediaMatches.md
 *
 * If you want to perform some action when the breakpoint changes, you can listen for the
 * breakpoint-change event on the window object.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  // your code here
 * });
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */


function isMobile(){

  const userAgent = navigator.userAgent.toLowerCase();
  
  // Check if the platform string contains any keywords indicating a mobile platform
  const Mobile = /iphone|ipad|ipod|android/.test(userAgent);

  return Mobile;
  
}isMobile();




    function triggerResize() {
      var event = new Event('resize');
      // Dispatch the event on the window object
      window.dispatchEvent(event);
    }

    // Initial setup
    var activateBundleSizeHeight = () => {
      window.setTimeout(function(){
        adjustBundleSize();
      },500);
    }

    window.setTimeout(function(){
      triggerResize();
    },1000);

    activateBundleSizeHeight();

    // Handle window resize event
    $(window).resize(function() {
      activateBundleSizeHeight();
    });

    function adjustBundleSize() {
      var ob = $('.hp-bundle-item.slick-current.slick-active .bundlemain-image').height();
      var bundleContent = 1 * ob; 
      if(window.innerWidth > 768){
        $('.homecustom-bundle .bundle_content').css('height', bundleContent + 'px');
      }
     
    }

    // -- Open full list of colors
    var bodyClass = $('body').attr('class');
    if (bodyClass.indexOf('collection') > -1){
      if ($('.filter__more').length > 0){
        window.setTimeout(function(){
          $('.filter__more').trigger('click');
        },3000);
      };
    }

document.querySelectorAll(".categories-menu .sub-menues-items").forEach(function (menu) {
  menu.addEventListener("click", function () {
    let id = this.getAttribute("id");

    document.querySelectorAll(".popup_content .pop_content_main").forEach(function (popup) {
      let popupId = popup.getAttribute("id");

      if (id === popupId) {
        let windowWidth = window.innerWidth;
        if(windowWidth < 769){
        let rightText = popup.querySelector(".popup-text .right");
        let categoriesHeight = document.querySelector(".brand-categories").offsetHeight;
        let lastImageHeight = document.querySelector(".categories-images.grow.w-full.last-image").offsetHeight;
        let totalHeight = parseInt(categoriesHeight) - parseInt(lastImageHeight);
        popup.style.height = rightText ? categoriesHeight - 120 + "px" : totalHeight - 140 + "px";
        }
        document.querySelector(".categories-menues-popup").classList.remove("hide");
        popup.closest(".popup_content").classList.add("is-active");

        let dataIndex = popup.closest(".popup_content").dataset.index;

        document.querySelectorAll(".page-btn").forEach(function (btn) {
          let pageBtnIndex = btn.dataset.index;
          btn.setAttribute("aria-current", dataIndex === pageBtnIndex);
        });
      } else {
        popup.closest(".popup_content").classList.remove("is-active");
      }
    });
  });
});

document.querySelectorAll(".popup_content .close-btn").forEach(function(closebtn) {
  closebtn.addEventListener("click", function() {
     this.closest(".popup_content").classList.remove("is-active");
     document.querySelector(".categories-menues-popup").classList.add("hide");
  });
});

document.querySelectorAll(".categories-menues-popup .slideshow-ctrl .page-btn").forEach(function(pageBtn) {

  pageBtn.addEventListener("click", function() {
    let index = this.dataset.index;
    let slideshow = this.closest("slide-show");
    slideshow.querySelectorAll(".popup_content").forEach(function(popIndex) {
      let popupIndex = popIndex.dataset.index;
      let dotsIndex = slideshow.querySelector('[data-index="' + popupIndex + '"]');
      dotsIndex.setAttribute("aria-current", index == popupIndex);
      if(index == popupIndex ){
      popIndex.classList.add("is-active");
      let windowWidth = window.innerWidth;
      if(windowWidth < 769){
      let rightText = popIndex.querySelector(".popup-text .right");
      let categoriesHeight = document.querySelector(".brand-categories").offsetHeight;
      let lastImageHeight = document.querySelector(".categories-images.grow.w-full.last-image").offsetHeight;
      let totalHeight = parseInt(categoriesHeight) - parseInt(lastImageHeight);
      popIndex.querySelector(".pop_content_main").style.height = rightText ? categoriesHeight - 120 + "px" : totalHeight - 140 + "px";
      }
      }
      else{
      popIndex.classList.remove("is-active");
      }
    });
  });
});

document.querySelectorAll(".popup_content").forEach((pop, index) => {
  pop.dataset.index = index;
});
        
let expandMore = document.querySelector(".expand_more");
let expandLess = document.querySelector(".expand_less");
let articleDescription = document.querySelector(".about_article_description");
if(expandMore){
expandMore.addEventListener("click", function(){
  this.style.display = "none";
  if(articleDescription){
  articleDescription.style.display = "block";
  }
  if(expandLess){
  expandLess.style.display = "block";
  }
});
}
if(expandLess){
expandLess.addEventListener("click", function(){
  this.style.display = "none";
  if(articleDescription){
  articleDescription.style.display = "none";
  }
  if(expandMore){
  expandMore.style.display = "block";
  }
});
}

document.querySelectorAll(".product-tags .tags-list").forEach((tag) => {
  tag.addEventListener("click", function() {
    const getHeaderOffset = () => {
      const cssHeaderHeight = parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue("--header-height")
      );
      if (!Number.isNaN(cssHeaderHeight) && cssHeaderHeight > 0) return cssHeaderHeight;

      const header = document.getElementById("shopify-section-header");
      const announcement = document.getElementById("shopify-section-announcement");
      let height = 0;
      if (header) height += header.offsetHeight;
      if (announcement) height += announcement.offsetHeight;
      return height;
    };

    // if(!$(this).find(".tags-popup").hasClass('open')){
    //     // close all
    //     $(".product-tags .tags-list").each((indx,val)=>{
    //       $(val).find(".tags-popup").slideUp(0);
    //       $(val).find(".tags-popup").removeClass('open');
    //       $(val).removeClass('open');
    //     });
    //     $(this).find(".tags-popup").slideDown(300, function() {
    //         $(this).css('display', 'flex');
    //     });
    //     $(this).find(".tags-popup").addClass('open')
    //     $(this).addClass('open');

    //     $('html, body').animate({
    //          scrollTop: $('.product-data').offset().top - 500
    //     }, 500);
    // }
    // else{
    //   $(this).find(".tags-popup").slideUp(0);
    //   $(this).find(".tags-popup").removeClass('open');
    //   $(this).removeClass('open');
    // }
    
    // Toggle 'open' class for the clicked element
    const popup = this.querySelector(".tags-popup");
    const wasOpen = this.classList.contains("open");

    popup.classList.toggle("open");
    this.classList.toggle("open");

    // Close popups and remove 'open' class from siblings
    document.querySelectorAll(".product-tags .tags-list").forEach((sibling) => {
      if (sibling !== this) {
        const siblingPopup = sibling.querySelector(".tags-popup");
        if (siblingPopup) siblingPopup.classList.remove("open");
        sibling.classList.remove("open");
      }
    });

    // Keep the page position fixed, but when opening a tab
    // reset the popup's own scroll so the heading is visible.
    if (!wasOpen && popup) {
      popup.scrollTop = 0;

      // Some layouts scroll a nested container instead of the popup itself.
      const innerScrollable = popup.querySelector(
        ".pop_content_main, .tags-popup__content, .tags-popup-content"
      );
      if (innerScrollable) innerScrollable.scrollTop = 0;
    }

    // If the heading is not visible (e.g. user is mid-content),
    // scroll JUST ENOUGH to reveal it (no smooth animation).
    if (!wasOpen) {
      const headerOffset = getHeaderOffset() + 12;
      const rect = this.getBoundingClientRect();
      const isAbove = rect.top < headerOffset;
      const isBelow = rect.bottom > window.innerHeight;

      if (isAbove || isBelow) {
        const top = rect.top + window.scrollY;
        const target = Math.max(0, top - headerOffset);
        window.scrollTo({ top: target, left: window.scrollX, behavior: "auto" });
      }
    }
  });
});

function updateMediaPadding() {
  var windowWidth = window.innerWidth;
  var mediaViewer = document.querySelector(".media-viewer");
  if (windowWidth > 769) {
    if (mediaViewer){
    var productImageHeight = document.querySelector(".media-viewer").offsetHeight;
    // console.log(productImageHeight);
    if (document.querySelector(".media-thumbs")) {
      document.querySelector(".media-thumbs").style.maxHeight = productImageHeight + "px";
    }
  }
  } else {
  }
}
window.addEventListener("load", updateMediaPadding);
window.addEventListener("resize", updateMediaPadding);

function toggleReadMore() {
  const descriptionContainer = document.querySelector('.product-description.desktop-description');
  const readMoreBtn = document.getElementById('readMoreBtn');

  if (descriptionContainer.style.maxHeight) {
    // Description is expanded, so collapse it
    descriptionContainer.style.maxHeight = null;
    readMoreBtn.textContent = 'קרא/י עוד';
  } else {
    // Description is collapsed, so expand it
    descriptionContainer.style.maxHeight = descriptionContainer.scrollHeight + 'px';
    readMoreBtn.textContent = 'סגור';
  }
}

function toggleReadMoreMolie() {
  const descriptionContainer = document.querySelector('.product-description.mobile-desciption');
  const readMoreBtn = document.getElementById('readMoreBtnmob');

  if (descriptionContainer.style.maxHeight) {
    // Description is expanded, so collapse it
    descriptionContainer.style.maxHeight = null;
    readMoreBtn.textContent = 'קרא/י עוד';
  } else {
    // Description is collapsed, so expand it
    descriptionContainer.style.maxHeight = descriptionContainer.scrollHeight + 'px';
    readMoreBtn.textContent = 'סגור';
  }
}

const uniqueSwatches = {};
document.querySelectorAll("details-disclosure.remove_filterpmcustomcolor_options .filter__swatches li").forEach((swatch) => {
  const swatchValue = swatch.dataset.value;
  if (uniqueSwatches[swatchValue]) {
    swatch.classList.add('hide');
  } else {
    uniqueSwatches[swatchValue] = true;
  }
});

window.onload = function() {
  if (isMobile()) {
    document.body.classList.add("isMobile");
  }

  // 15/6/25 -- TEMPORARY REDIRECT -- NEEDS TO BE REMOVED ONCE HEBREW LANGUAGE IS ALL SET 
  //if (location.href.indexOf('https://bone-street.com/he') > -1){
  //  location.href="https://bone-street.com/";
  //}
  
};



  // /* 
  //    Creating paging mutation on collection in order to run filter tags on a Sleep & Rest template
  //    When items are spread over a few pages. 
  //    Each page load will run the filtering function to show only what is selected
  // */

  // let selectedTagFilter = "";

  // // Select the parent element
  // const collectionGridElement = document.querySelector('.main-products-grid__results ul');
  
  // // Define the function to be executed when a direct sibling is added
  // function handleSiblingAddition(mutationsList) {
  //     for (const mutation of mutationsList) {
  //         if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {

  //             //console.log('Load new page');
  //             window.setTimeout(function(){
  //               refreshContent(selectedTagFilter);
  //             },500);
  //         }
  //     }
  // }

  // if (collectionGridElement){
  //     // Create a MutationObserver instance
  //     const observer = new MutationObserver(handleSiblingAddition);
      
  //     // Configure the observer to listen for child node additions
  //     const config = { childList: true };
      
  //     // Start observing the parent element
  //     observer.observe(collectionGridElement, config);
  // }
  

  // let collections = document.querySelectorAll(".custom-subcat");
  // collections.forEach((collection) => {
  //   collection.addEventListener("click", function () {
  //     let collectionValue = this.dataset.value;
  //     selectedTagFilter = collectionValue;
  //     collections.forEach((sibling) => {
  //       if (sibling !== this) {
  //         sibling.classList.remove("active");
  //       }
  //     });
  //     this.classList.toggle("active");
  //     // Check if any element has the "active" class
  //   const hasActiveElement = Array.from(collections).some((elem) => elem.classList.contains("active"));

  //   if (hasActiveElement) {
  //     refreshContent(collectionValue);
  //   } else {
  //     // Call a function to reset the results when no element is active
  //     resetResults();
      
  //     // show all media elemnts that were hidden
  //     $("li[class^='media_element']").show();
  //   }
  
  //   document.querySelector(".main-products-grid__results").classList.add('is-loading');

  //   // Remove loading class after 2 seconds (adjust the delay as needed)
  //   setTimeout(() => {
  //     document.querySelector(".main-products-grid__results").classList.remove('is-loading');
  //   }, 2000); // 2000 milliseconds = 2 seconds
  //   });
  // });


  // function refreshContent(selectedTags) {
  //   if (selectedTags == ""){
  //     return;
  //   }
  //   let updatedTags = selectedTags.replace(/&/g, '%26'); // Encode ampersand to %26
  //   updatedTags = updatedTags.replace(/ /g, "_"); // Replace spaces with underscores
  //   //console.log(updatedTags);
  //   var url = new URL(window.location);
     
  //   history.pushState()
  //   fetch(`/collections/all?constraint=${updatedTags}&view=product-selector`, {
  //     method: 'GET'
  //   })
  //   .then((response) => response.json())
  //   .then((data) => {

  //     // get all ids (variants and products) // Ziv Fix Jun 14th
  //     const resultProductIds = data.flatMap(item => [
  //       item.id,
  //       ...item.variants.map(variant => variant.id)
  //     ]);

  //     // hide all media elemnts except 1
  //     $("li[class^='media_element']").hide();
      
  //     //let resultProductIds = data.map(item => item.id);
  //     //console.log(resultProductIds);

  //     let products = document.querySelectorAll(".main-products-grid__results .js-pagination-result");
      
  //     products.forEach((product) => {
        
  //       let productId = product.getAttribute('data-product-id');

  //       if (resultProductIds.includes(parseInt(productId))) {
  //         product.classList.remove("hide");
  //       } 
  //       else{
  //         product.classList.add("hide");
  //       }
  //     });
  //   })
  //   .catch((error) => {
  //     console.error('Error fetching data:', error);
  //   });
  // }
  

  // function resetResults(){
  //   let products = document.querySelectorAll(".main-products-grid__results .js-pagination-result");
  //   products.forEach((product) => {
  //     product.classList.remove("hide");
  //   });
  //   let selectedTagFilter = "";
  // }


  let mobileCartIcon = document.querySelector(".cart-icon #cart-icon");
  mobileCartIcon.addEventListener("click", function(event){
   event.preventDefault();
   document.body.classList.add("fixed");
   document.querySelector(".drawer--cart").setAttribute("aria-hidden", "true");
   document.querySelector(".drawer--cart").setAttribute("open", "open");
  });

  let mobileCartIconClose = document.querySelector(".drawer--cart .js-close-drawer");
  mobileCartIconClose.addEventListener("click", function(event){
    event.preventDefault();
    document.body.classList.remove("fixed");
    document.querySelector(".drawer--cart").setAttribute("aria-hidden", "false");
    document.querySelector(".drawer--cart").removeAttribute("open", "open");
   });


   window.addEventListener('scroll', function() {
    var stickyHeader = document.querySelector('.cc-header--sticky'); 
    let storeHeader = document.querySelector("store-header");
    let announcementBar =document.querySelector(".announcement");
    let storeHeaderHeight = storeHeader.offsetHeight;
    if (window.scrollY > 100) {
      stickyHeader?.classList.add("stickyHeader");
      announcementBar?.classList.add("stickyHeader");
      document.body.style.paddingTop = storeHeaderHeight +  'px';
    } else {
      stickyHeader?.classList.remove("stickyHeader");
      announcementBar?.classList.remove("stickyHeader");
      document.body.style.paddingTop = '0';
    }
  });
let pathname = window.location.href;
// console.log(pathname);
 if(pathname.has('filter.p.m.custom.color_options')){
  let activeFilters = document.querySelectorAll(".active-filters a");
  let variantColorCards = document.querySelectorAll(".variant-color-products");
  let cardSwatches = document.querySelectorAll(".card__swatches input");
  const seenValues = new Set();
  
  var filterObj = this;
  
  activeFilters.forEach(filter => {
      let filterValue = filter.getAttribute("data-value");
  
      if (seenValues.has(filterValue)) {
          filter.classList.add('hide');
      } else {
          seenValues.add(filterValue);
          filter.classList.remove('hide');
          filter.addEventListener("click", function(evt) {
              filterObj.removeOtherFilters(evt, filterValue, searchParams);
          });
      }
  
      cardSwatches.forEach(input => {
          let inputValue = input.getAttribute("value");
  
          if (inputValue.includes(filterValue)) {
              input.click();
  
              // Variant image swap logic
              const productCard = input.closest('product-card'); // Get the closest product card element
              if (productCard) {
                  const variantMediaId = input.getAttribute('data-media-id'); // Assume each input has a data-media-id attribute
  
                  if (variantMediaId) {
                      const variantMedia = productCard.querySelector(`[data-media-id="${variantMediaId}"]`);
  
                      if (variantMedia) {
                          const images = productCard.querySelectorAll('.card__main-image');
                          images.forEach(image => {
                              image.hidden = true;
                          });
                          variantMedia.hidden = false;
                      }
                  }
  
                  // const separator = productCard.productUrl.split('?').length > 1 ? '&' : '?';
                  // const url = `${productCard.productUrl + separator}variant=${input.getAttribute('data-variant-id')}`;
  
                  // // Update link hrefs to the URL of the selected variant
                  // const links = productCard.querySelectorAll('.js-prod-link');
                  // links.forEach(link => {
                  //     link.href = url;
                  // });
  
                  // Update the Quick Add button data
                  const quickAddBtn = productCard.querySelector('.js-quick-add');
                  if (quickAddBtn) {
                      quickAddBtn.dataset.selectedColor = inputValue;
                  }
              }
          }
      });
  });

function removeOtherFilters(event, color) {
  const links = document.querySelectorAll(`a[data-value="${color}"]`);
  links.forEach(link => {
      if (link !== event.currentTarget) {
          link.remove();
      }
  });
}

  if (activeFilters.length > 0 && variantColorCards.length > 0) {
      variantColorCards.forEach(card => {
          let colorCategory = card.getAttribute("data-category");
          let shouldShow = false;
  
          activeFilters.forEach(filter => {
              let filterValue = filter.getAttribute("data-value");
              if (colorCategory === filterValue) {
                  shouldShow = true;
              }
          });
  
          if (shouldShow) {
              card.classList.remove("hide");
          } else {
              card.classList.add("hide");
          }
      });
  }
 }

//--- Coming Soon place holder query string
//--- The code accepts the brand name as query string and displays it accordignly:
//--- for example: https://bone-street.com/pages/coming-soon?p=Dogs_Horses


$(document).ready(function(){

  if ($('body').attr('class').indexOf('coming-soon') > -1){
    // console.log('coming soon');
    var a = location.href;
    var p = '';  
    if (a.indexOf('?p=')>-1){
      p = a.split('?p=')[1];

      // -- for the dogs & horses form
      if (p.indexOf('_')>-1){
        p = p.replaceAll('_',' ');
      }

      if (p.toLowerCase().indexOf('and')>-1){
        p = p.replaceAll('and','&');
      }
      
      // console.log(p);

      // -- main text
      if ($('.section__block').text().indexOf('<placeholder>') > -1){
        var newContent = $('.section__block').text().replace('<placeholder>', p);
        $('.section__block').text(newContent);
      }

      // -- newsletter form
      if ($('.newsletter_form .desc p').text().indexOf('<placeholder>') > -1){
        var newContent = $('.newsletter_form .desc p').text().replace('<placeholder>', p);
        $('.newsletter_form .desc p').text(newContent);
      }
      
      
    }
  }

  if ($('.shopify-policy__container').length > 0){
    $(".shopify-policy__container h2").each(function() {
        var content = $(this).html();
        $(this).replaceWith('<p>' + content + '</p>');
    });
  }

  var currentVariant = $(".product_swatch--block").data("current-variant");
  var onloadCurrentVariantQty = $(".product_swatch--block .product_variant--item[data-variant='"+currentVariant+"']").data("qty");
  var notifyME = $(".product_swatch--block .product_variant--item[data-variant='"+currentVariant+"']").data("discard-variant");

  if(notifyME == false && getCurrentQty() <= 0 && getCurrentQty() != null ){
    // console.log(getCurrentQty());
    $(".product-info__add-button .added_to--cart").text("Sold Out");
    $(".product-info__add-button .added_to--cart").show();
    var interval = setInterval(function() {
      if($(".restock-alerts-notify-button").length) {
        $(".restock-alerts-notify-button").hide();
        clearInterval(interval);
      }
    }, 1000);
  }


  function getCurrentQty(){

    const productSwatchBlock = document.querySelector('.product_swatch--block');
    
    if (productSwatchBlock) {
        const currentVariant = productSwatchBlock.getAttribute('data-current-variant');
        const variantItems = productSwatchBlock.querySelectorAll('.product_variant--item');
        let qty = null;
        variantItems.forEach(item => {
            const variantName = item.getAttribute('data-variant');
            if (variantName === currentVariant) {
                qty = item.getAttribute('data-qty');
            }
        });

        return qty;
    }
    
    return 0;
    
  }
  
  if (window.location.href === "https://bone-street.com/collections" || window.location.href.indexOf("collections/all-collection") > -1) {
    $(window).on('scroll', function() {
      $('[loading="lazy"]').each(function() {
        var dataSrcSet = $(this).attr('data-srcset');
        if (dataSrcSet) {
          $(this).attr('srcset', dataSrcSet);
        }
      });
    });
  }

  if (location.href.indexOf('codeapplied=') > -1){
    const code = location.href.split('codeapplied=')[1];
    const msg = $('<div class="couponapplied">קוד קופון ' + '<strong>' + code + '</strong>' + ' הוזן בהצלחה</div>');
    $('.shopify-section.shopify-section-group-header-group.cc-announcement').append(msg);
  }
  
    $('.couponapplied').slideDown(1000, function() {
      window.setTimeout(function(){$('.couponapplied').slideUp(1000)},3000);
    });
  
});

  

  function shopifyCollectionHandle(pathname) {
    if (!pathname || typeof pathname !== 'string') return '';
    const parts = pathname.split('/').filter(Boolean);
    const idx = parts.indexOf('collections');
    if (idx === -1 || !parts[idx + 1]) return '';
    const raw = parts[idx + 1].split('?')[0];
    try {
      return decodeURIComponent(raw);
    } catch (e) {
      return raw;
    }
  }

  /** e.g. /he from /he/collections/foo — matches Shopify language URL first segment */
  function getStorefrontLocalePrefix() {
    const path = window.location.pathname;
    const m = path.match(/^\/([a-z]{2}(?:-[a-z]{2})?)(?=\/|$)/i);
    if (!m) return '';
    return '/' + m[1];
  }

  /**
   * Metafield URLs are often canonical (no /he). Rewrite same-origin storefront paths
   * to keep the current language prefix when browsing a localized storefront.
   */
  function localizeShopifyStoreUrl(href) {
    if (!href || typeof href !== 'string') return href;
    try {
      const u = new URL(href, window.location.href);
      if (u.origin !== window.location.origin) return href;
      const prefix = getStorefrontLocalePrefix();
      if (!prefix) return href;
      let p = u.pathname;
      if (p === prefix || p.startsWith(prefix + '/')) return u.toString();
      if (/^\/cdn\//i.test(p)) return href;
      u.pathname = prefix + (p.startsWith('/') ? p : '/' + p);
      return u.toString();
    } catch (e) {
      return href;
    }
  }

  const listItem = document.querySelectorAll('.custom-subcat');
  listItem.forEach(item => {
    const parentRaw = item.getAttribute('data-parent-category');
    if (parentRaw) {
      item.setAttribute('data-parent-category', localizeShopifyStoreUrl(parentRaw));
    }
    const anchor = item.querySelector('a');
    if (anchor) {
      const h = anchor.getAttribute('href');
      if (h) anchor.setAttribute('href', localizeShopifyStoreUrl(h));
    }
  });

  const currentCollectionHandle = shopifyCollectionHandle(window.location.pathname);
  listItem.forEach(item => {
    const anchor = item.querySelector('a');
    if (!anchor) return;
    const href = anchor.getAttribute('href');
    if (!href) return;
    let linkPath;
    try {
      linkPath = new URL(href, window.location.href).pathname;
    } catch (e) {
      return;
    }
    const linkHandle = shopifyCollectionHandle(linkPath);
    if (currentCollectionHandle && linkHandle && currentCollectionHandle === linkHandle) {
      item.classList.add('active');
    }
    const parentHref = item.getAttribute('data-parent-category');
    if (item.classList.contains('active') && parentHref) {
      anchor.setAttribute('href', parentHref);
    }
  });

  function isIOSDevice() {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  }
  
  // Usage example:
  if (isIOSDevice()) {
    document.querySelector("body").classList.add("ios-devices");
  } else {
    console.log("This is not an iOS device.");
  }
 



    const toolbar = document.getElementById("products-toolbar");
    let mobileBottom = document.querySelector(".mobile-bottom");
    let storeHeader = document.querySelector("store-header");
    if(toolbar){
    const toolbarOffset = toolbar.offsetTop; // Get the initial position of the toolbar
    window.addEventListener("scroll", function() {
      if(window.innerWidth < 768){
      if (window.scrollY >= toolbarOffset) {
        toolbar?.classList.add("sticky"); // Add sticky class when scrolled past the toolbar
      } else {
        toolbar?.classList.remove("sticky"); // Remove sticky class when scrolled back up
      }
    }
    });
  }
  window.addEventListener("scroll", function() {
  if(window.innerWidth < 768){
  if (window.scrollY >= 100) {
    mobileBottom?.classList.add("hide");
    storeHeader?.classList.add("sticky");
  } else {
    mobileBottom?.classList.remove("hide");
    storeHeader?.classList.remove("sticky");''
  }
  }
  });

    let resultCount = document.querySelector(".js-close-drawer-mob");
    if(resultCount){
      let resultCountHTML = resultCount.innerHTML;
      let count = resultCountHTML.replace(/\D/g, ''); // Removes all non-digit characters
      document.querySelector(".collection-banner__count").innerHTML = "("+count+") Products";
    }


    const mediaItems = document.querySelectorAll('.media-thumbs__item');
    const activeItem = Array.from(mediaItems).find(item => item.classList.contains('active'));
    
    if (activeItem) {
      mediaItems.forEach(item => {
        if (item === activeItem) {
          return; // Skip the active item itself
        }
    
        if (item.compareDocumentPosition(activeItem) & Node.DOCUMENT_POSITION_FOLLOWING) {
          item.classList.add('common-images'); // Add the class
    
          // Move this item to the end of its parent container
          item.parentNode.appendChild(item);
        }
      });
    }
    

      const mediaViewerItems = document.querySelectorAll('.media-viewer__item');
      const activemediaViewerItem = Array.from(mediaViewerItems).find(item => item.classList.contains('active'));
      if (activemediaViewerItem) {
        mediaViewerItems.forEach(item => {
          if (item === activemediaViewerItem) {
            return; 
          }
          if (item.compareDocumentPosition(activemediaViewerItem) & Node.DOCUMENT_POSITION_FOLLOWING) {
            item.classList.add('common-images');
          }
        });
      }

      window.addEventListener('scroll', function() {
        var btn = document.querySelector('.back-to-top-icon'); // Assuming .btn is the class for your button
        if (window.scrollY > 300) {
          btn.classList.add('show');
        } else {
          btn.classList.remove('show');
        }
      });

     
      const productPrice = document.querySelector(".product-info__block--sm.product-price");
      const productBuyButton = document.querySelector(".product-buy-buttons");
      const productPolicyButton = document.querySelector(".product-policies");

const targetElement = document.querySelector(".product-description.desktop-description");
// console.log(targetElement);
window.addEventListener("scroll", () => {

  // disable sticky button for now -- Ziv Nov 19
  return;
  
  if(window.innerWidth > 768 ){
  const targetPosition = targetElement.getBoundingClientRect().top;
  // console.log(targetPosition);
  const threshold = 200;
  if (targetPosition <= threshold - 300 ) {
    productPrice.classList.add("fixed");
    productBuyButton.classList.add("fixed");
    productPolicyButton.classList.add("fixed");
    let policiesHeight = productPolicyButton.clientHeight;
    productBuyButton.style.bottom = policiesHeight + "px";
    let BuyButtonHeight = productBuyButton.clientHeight;
    productPrice.style.bottom = policiesHeight + BuyButtonHeight + "px";
  } else {
    productPrice.classList.remove("fixed");
    productBuyButton.classList.remove("fixed");
    productPolicyButton.classList.remove("fixed");
  }
}
});

const targetElementMobile = document.querySelector(".product-description.mobile-desciption");
// console.log(targetElementMobile);
window.addEventListener("scroll", () => {

  // disable sticky button for now -- Ziv Nov 19
  return;
  
if(window.innerWidth <= 768 ){
  const targetPosition = targetElementMobile.getBoundingClientRect().top;
  console.log(targetPosition);
  const threshold = 200;
  if (targetPosition <= threshold - 300 ) {
    productPrice.classList.add("fixed");
    productBuyButton.classList.add("fixed");
    productPolicyButton.classList.add("fixed");
    let policiesHeight = productPolicyButton.clientHeight;
    productBuyButton.style.bottom = policiesHeight + "px";
    let BuyButtonHeight = productBuyButton.clientHeight;
    productPrice.style.bottom = policiesHeight + BuyButtonHeight + "px";
  } else {
    productPrice.classList.remove("fixed");
    productBuyButton.classList.remove("fixed");
    productPolicyButton.classList.remove("fixed");
  }
}
});
      

setTimeout( function(){
  const mediaGallery = document.querySelector(".media-gallery");  
  if (mediaGallery) {  
    mediaGallery.style.opacity = 1;  
  }
}, 1000);



// Function to check if a string contains Hebrew characters  
function isHebrew(text) {  
  const hebrewRegex = /[\u0590-\u05FF]/;  
  return hebrewRegex.test(text);  
}  

// Function to check if an element directly contains text  
function hasDirectTextNode(element) {  
  for (const child of element.childNodes) {  
    if (child.nodeType === Node.TEXT_NODE && child.nodeValue.trim() !== '') {  
      return true;  
    }  
  }  
  return false;  
}  

  // Function to iterate through all <div>, <span>, <p> elements and assign the correct class  
function assignLanguageClasses(parentId = 'body') {  
  // Get the parent element (default is <body>)  
  const parentElement = parentId === 'body' ? document.body : document.getElementById(parentId);  

  if (!parentElement) {  
    console.error(`Element with id "${parentId}" not found.`);  
    return;  
  }  

  // Select all relevant elements within the parent element  
  const elements = parentElement.querySelectorAll('div, span, p, h1, h2, h3, h4, h5, a');  

  elements.forEach(element => {  
    if (hasDirectTextNode(element)) {  
      const textContent = Array.from(element.childNodes)  
        .filter(node => node.nodeType === Node.TEXT_NODE)  
        .map(node => node.nodeValue.trim())  
        .join(' '); // Combine all direct text nodes  

      if (isHebrew(textContent)) {  
        // Add he class  
        element.classList.add('langhe');  
      }  
    }  
  });  
} 

// Add HE class to Hebrew fonts containers  
document.addEventListener('DOMContentLoaded', function() {  

  assignLanguageClasses(); 
  
});

// Cart gift object
function modifyCartGiftItemText() {

  // console.log('cart gift block process');
  
  // Locate the element with id "rebuy-cart"  
  const cartElement = document.getElementById("rebuy-cart");  
  if (!cartElement) {  
    return;  
  }  

  const cartItems = cartElement?.getElementsByClassName("rebuy-product-block");  
  if (!cartItems.length) {  
    // console.log('cart gift block not found');
    return;  
  }  

  // Iterate through the cart items to find the <a> element with the specific text  
  for (const item of cartItems) {  
    const productTitleLink = item.querySelector(".rebuy-product-info a.rebuy-product-title");  
    if (productTitleLink && productTitleLink.textContent.includes("|")) {  
      const [beforePipe, afterPipe] = productTitleLink.textContent.split("|").map(str => str.trim());  

      // Update the text content and wrap the part after the pipe in a <span>  
      productTitleLink.innerHTML = `${beforePipe}  <div class="afterpipetitle langhe">${afterPipe}</div>`;  
    }  
  }  

}

// Cart line item
function modifyCartItemText() {  
  // Locate the element with id "rebuy-cart"  
  const cartElement = document.getElementById("rebuy-cart");  
  if (!cartElement) {  
    console.error("Element with id 'rebuy-cart' not found.");  
    return;  
  }  

  // Find all <li> elements with the class "rebuy-cart__flyout-item" inside the cart  
  const cartItems = cartElement?.getElementsByClassName("rebuy-cart__flyout-item");  
  if (!cartItems.length) {  
    console.error("No elements with class 'rebuy-cart__flyout-item' found.");  
    return;  
  }  

  // Iterate through the cart items to find the <a> element with the specific text  
  for (const item of cartItems) {  
    const productTitleLink = item.querySelector("a.rebuy-cart__flyout-item-product-title");  
    if (productTitleLink && productTitleLink.textContent.includes("|")) {  
      const [beforePipe, afterPipe] = productTitleLink.textContent.split("|").map(str => str.trim());  

      // Update the text content and wrap the part after the pipe in a <span>  
      productTitleLink.innerHTML = `${beforePipe}  <div class="afterpipetitle langhe">${afterPipe}</div>`;  
    }  
  }  

  // console.log("All matching elements have been updated.");  
}  


// Callback function for when the cart is ready.
function smartcartReady(){
  // console.log('cart ready');
  modifyCartItemText();
  window.setTimeout(()=>{modifyCartGiftItemText();},1000);
  window.setTimeout(()=>{assignLanguageClasses("rebuy-cart");},2000); // assign classes under cart ID when its ready
}

function strip(v){
  v = v.replace(',00','');
  v = v.replace('.','');
  v = v.replace(',','.');
  v = v.replace(/[^\d.]/g, '');  
  return v;
}

function calculateSavings(){  
  // console.log('calculateSavings activated');  
  document.querySelectorAll('.rebuy-cart__flyout-item-price').forEach(priceEl => {  
      if (priceEl.querySelector('.compare-at')) {  
          // Find the sibling .rebuy-cart__flyout-item-quantity  
          const parent = priceEl.parentElement;  
          const label = parent.querySelector('.rebuy-cart__flyout-item-quantity-widget-label');  
          let qty = '';  
          if (label) {  
              for (let node of label.childNodes) {  
                  if (node.nodeType === 3) {// TEXT_NODE  
                      const text = node.textContent.trim();  
                      if (text) {  
                          qty = text;  
                          break;  
                      }  
                  }  
              }  
          }  
         
          const saleSpan = priceEl.querySelector('.rebuy-money.sale span:nth-of-type(2)');  
          const compareAtSpan = priceEl.querySelector('.rebuy-money.compare-at span:nth-of-type(2)');  
          const saleText = saleSpan ? saleSpan.textContent.trim() : '';  
          const compareAtText = compareAtSpan ? compareAtSpan.textContent.trim() : '';  
          const saleValue = parseFloat(strip(saleText));  
          const compareAtValue = parseFloat(strip(compareAtText));  
          // if (!isNaN(saleValue) && !isNaN(compareAtValue)) {  
          //     console.log(`Qty: ${qty} | Sale: ${saleValue} | Compare-at: ${compareAtValue} | Diff: ${saleValue - compareAtValue}`);  
          // } else {  
          //     console.log('Invalid sale or compare-at price');  
          // }  
      }  
  });   
}  

function updateMediaPadding() {
  var windowWidth = window.innerWidth;
  var mediaViewer = document.querySelector(".media-viewer");
  if (windowWidth > 769) {
    if (mediaViewer){
    var productImageHeight = document.querySelector(".media-viewer").offsetHeight;
    // console.log(productImageHeight);
    if (document.querySelector(".media-thumbs")) {
      document.querySelector(".media-thumbs").style.maxHeight = productImageHeight  + "px";
    }
    const productVideos = mediaViewer.querySelectorAll("video");
    productVideos.forEach(video => {
      video.style.height = productImageHeight  + "px";
    });
  }
  } else {
  }
}
window.addEventListener("load", updateMediaPadding);
window.addEventListener("resize", updateMediaPadding);


// Target the deferred-media elements
const deferredMedias = document.querySelectorAll('deferred-media');

deferredMedias.forEach(deferred => {
  const observer = new MutationObserver(() => {
    const video = deferred.querySelector('video');
    if (video) {
      applyVideoStyling(video);
      observer.disconnect(); // Stop observing once video is found and styled
    }
  });

  observer.observe(deferred, { childList: true, subtree: true });

});

function applyVideoStyling(video) {
  const container = document.querySelector(".media-viewer");
  if (container) {
    video.style.height = container.offsetHeight + 'px';
  }

  const setObjectFit = () => {
    video.style.objectFit = video.videoWidth < 650 ? 'cover' : 'contain';
    console.log('Video loaded:', video.videoWidth, video.videoHeight);
  };

  if (video.readyState >= 1) {
    setObjectFit();
  } else {
    video.addEventListener('loadedmetadata', function handler() {
      setObjectFit();
      video.removeEventListener('loadedmetadata', handler);
    });
  }
}

function hideProgressBarWhenEmpty() {
  const progressBar = document.querySelector('[data-rebuy-component="progress-bar"]');
  if (!progressBar) return;
  
  if (Cart.cart.item_count > 0) {
    progressBar.style.display = "block";
  } else {
    progressBar.style.display = "none";
  }
}

document.addEventListener('rebuy:cart.change', function(event){
  // Your custom script here
  setTimeout(changePriceFormat, 500);
  setTimeout(changePriceFormat, 1000);
});

document.addEventListener('rebuy:cart.loaded', function (event) {
  hideProgressBarWhenEmpty();
  setTimeout(changePriceFormat, 100);
});

document.addEventListener('rebuy:smartcart.show', function(event){
  console.log('Smart Cart opened', event.detail);
  // Your custom price formatting code here
  setTimeout(changePriceFormat, 500);
  setTimeout(changePriceFormat, 1000);
});

hideProgressBarWhenEmpty();

function changePriceFormat() {

  const valueSpanSelectors = [
    '#rebuy-cart .rebuy-money span:not(.sr-only)',
    '.rebuy-money span:not(.sr-only)',
    '.rebuy-cart__flyout-subtotal-amount span'
  ];
  const wrapperSelectors = [
    '.money',
    '.price',
    '.product-price',
    '.rebuy-cart__flyout-subtotal-amount'
  ];

  // Convert comma-decimal → dot-decimal
  const toDotDecimal = function(text) {
    if (!text || text.indexOf(',') === -1) return text;

    return text.replace(/(\d{1,3}(?:[.\s\u00A0\u202F]\d{3})*|\d+),(\d{2})(?!\d)/g,
      function (_match, intPart, decPart) {
        const normalizedInt = intPart.replace(/[.\s\u00A0\u202F]/g, '');
        return normalizedInt + '.' + decPart;
      }
    );
  };

  // Remove trailing zeros
  const removeTrailingZeros = function(text) {
    return text
      .replace(/(\.\d*?[1-9])0+$/, '$1')
      .replace(/\.0+$/, '')
      .replace(/\.$/, '');
  };

  // ⭐ Add thousands separators
  const addThousands = function(text) {
    if (!text) return text;

    return text.replace(/\d+(?:\.\d+)?/g, function(numStr) {
      const parts = numStr.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return parts.join('.');
    });
  };

  const processText = function(text) {
    const converted = toDotDecimal(text);
    const cleaned = removeTrailingZeros(converted);
    return addThousands(cleaned);
  };

  // Process spans
  const valueSpans = document.querySelectorAll(valueSpanSelectors.join(','));
  valueSpans.forEach(function(span) {
    const before = span.textContent.trim();
    const after = processText(before);
    if (before !== after) {
      span.textContent = after;
    }
  });

  // Process wrappers without inner spans
  const wrappers = document.querySelectorAll(wrapperSelectors.join(','));
  wrappers.forEach(function(el) {
    if (el.querySelector('span')) return;

    const before = el.textContent.trim();
    const after = processText(before);
    if (before !== after) {
      el.textContent = after;
    }
  });

  
const progressLabel = document.getElementById('rebuy-cart__progress-bar-meter-label');

if (progressLabel) {
  progressLabel.innerHTML = progressLabel.innerHTML.replace(
    /(\d+),(00)(?!\d)/g,
    '$1'
  );
}
}





/****** NOTIFY ME ******/

// Notify function to change the text of the button
function watchRestockButton({
  containerSelector = 'div.product-info__add-to-cart',
  buttonSelector = 'button.restock-alerts-notify-button',
  newText = 'הודיעו לי כשחוזר למלאי'
} = {}) {
  const container = document.querySelector(containerSelector);
  if (!container) return { disconnect: () => {} };

  // Apply once for any existing buttons
  function apply(scope = container) {
    scope.querySelectorAll(buttonSelector).forEach(btn => {
      if (btn.innerText.trim() !== newText) btn.innerText = newText;
    });
  }
  apply();

  // Observe new additions
  const observer = new MutationObserver(mutations => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (!(node instanceof HTMLElement)) continue;

        // If the added node is the button itself
        if (node.matches?.(buttonSelector)) {
          if (node.innerText.trim() !== newText) node.innerText = newText;
          continue;
        }

        // If the added node may contain the button
        const found = node.querySelector?.(buttonSelector);
        if (found) apply(node);
      }
    }
  });

  observer.observe(container, { childList: true, subtree: true });

  // Return a way to stop observing if needed
  return {
    disconnect() { observer.disconnect(); }
  };
}

// Example usage:
document.addEventListener('DOMContentLoaded', () => {
  watchRestockButton({
    // containerSelector: 'div.product-info__add-to-cart',
    // buttonSelector: 'button.restock-alerts-notify-button',
    newText: 'הודיעו לי כשחוזר למלאי' // customize as needed
  });
});

// Notify me pop-up function to modify the text of the button and the title
$(function () {
  $(function () {

    setTimeout(function () {

      // Find the last immediate body > div that is empty and has no class
      const $target = $('body > div').filter(function () {
        const hasNoClass = !this.hasAttribute('class') || this.getAttribute('class').trim() === '';
        const isEmpty = this.children.length === 0 && this.textContent.trim() === '';
        return hasNoClass && isEmpty;
      }).last();

      if ($target.length) {
        $target.addClass('notifyme_popup');

        // Set up a MutationObserver to watch for child changes
        const el = $target[0];
        const observer = new MutationObserver(function (mutations) {
          mutations.forEach(function (m) {
            if (m.type === 'childList') {
              // Handle added nodes
              if (m.addedNodes && m.addedNodes.length) {
                 $('.notifyme_popup .restock-alerts-submit-button').text('הרשמה לעדכונים על המוצר');
                 $('.notifyme_popup .restock-alerts-form-title.formTitleStyle').text('שלחו לי תזכורת שחוזר למלאי');
                 $('button.restock-alerts-notify-button').text('הודיעו לי כשחוזר למלאי');
              }
              // Handle removed nodes
              if (m.removedNodes && m.removedNodes.length) {
                  $('button.restock-alerts-notify-button').text('הודיעו לי כשחוזר למלאי');
              }
            }
          });
        });

        // Observe direct children changes; set subtree:true if you want deeper descendants
        observer.observe(el, {
          childList: true,   // listen for children being added/removed
          subtree: false     // change to true to include all descendants
          // attributes: true // uncomment if you also want attribute changes
          // characterData: true // uncomment to detect text node changes
        });

        // Optional: store observer for later disconnect
        $(el).data('notifymeObserver', observer);
      }
    }, 2000);
  });
});

// Save scroll position when clicking on product links in collection pages
if (window.location.pathname.includes('/collections/')) {
  // Save scroll position when clicking on any product link
  document.addEventListener('click', (e) => {
    const productLink = e.target.closest('a[href*="/products/"]');
    if (!productLink) return;

    const collMatch = window.location.pathname.match(/^\/collections\/([^/?#]+)/);
    if (collMatch) {
      const handle = collMatch[1];
      try {
        sessionStorage.setItem(
          'pdpFromCollectionUrl',
          `${window.location.origin}/collections/${handle}`
        );
        sessionStorage.setItem('pdpFromCollectionHandle', handle);
        const titleEl = document.querySelector('[data-breadcrumb-collection-title]');
        sessionStorage.setItem(
          'pdpFromCollectionTitle',
          titleEl ? titleEl.textContent.trim() : ''
        );
      } catch (err) {}
    }

      // Find the product card element
      const productCard = productLink.closest('.js-pagination-result');
      if (productCard) {
        const productId = productCard.getAttribute('data-product-id');
        
        // Save current scroll position and URL immediately
        const currentScrollY = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop;
        
        // Calculate product's position relative to the results container
        const resultsContainer = document.getElementById('filter-results');
        let productOffsetTop = 0;
        if (resultsContainer && productCard) {
          productOffsetTop = productCard.offsetTop - (resultsContainer.offsetTop || 0);
        }
        
        // Only save if scroll position is valid (not at footer)
        const pageHeight = Math.max(
          document.body.scrollHeight,
          document.documentElement.scrollHeight
        );
        const maxScroll = pageHeight - window.innerHeight;
        
        if (currentScrollY > 0 && currentScrollY < maxScroll - 100) {
          sessionStorage.setItem('collectionScrollY', currentScrollY.toString());
          sessionStorage.setItem('collectionProductId', productId || '');
          sessionStorage.setItem('collectionProductOffset', productOffsetTop.toString());
          sessionStorage.setItem('collectionUrl', window.location.href);
        }
      }
  });

  // Function to restore scroll position
  function restoreScrollPosition() {
    const savedScrollY = sessionStorage.getItem('collectionScrollY');
    const savedProductId = sessionStorage.getItem('collectionProductId');
    const savedProductOffset = sessionStorage.getItem('collectionProductOffset');
    const savedUrl = sessionStorage.getItem('collectionUrl');
    
    // Only restore if we're on the same collection page
    if (savedUrl) {
      // Check if URLs match (accounting for query parameters)
      const currentUrl = window.location.href.split('?')[0];
      const savedUrlBase = savedUrl.split('?')[0];
      
      if (currentUrl === savedUrlBase) {
        const scrollY = parseInt(savedScrollY, 10);
        const productOffset = parseInt(savedProductOffset, 10);
        
        // Try to find the product element first (works with infinite scroll)
        const restoreToProduct = () => {
          if (savedProductId) {
            const productElement = document.querySelector(`.js-pagination-result[data-product-id="${savedProductId}"]`);
            if (productElement) {
              // Product found, scroll to it precisely
              const rect = productElement.getBoundingClientRect();
              const currentScroll = window.pageYOffset || document.documentElement.scrollTop;
              
              // Calculate exact scroll position to center the product with offset from top
              const targetScroll = currentScroll + rect.top - 120; // 120px offset from top for header
              
              window.scrollTo({
                top: Math.max(0, targetScroll),
                behavior: 'auto'
              });
              document.documentElement.scrollTop = Math.max(0, targetScroll);
              document.body.scrollTop = Math.max(0, targetScroll);
              
              return true; // Successfully restored
            }
          }
          return false; // Product not found
        };
        
        // Wait for page to be fully loaded before restoring
        const restoreAfterLoad = () => {
          // First try to find and scroll to the product element
          if (restoreToProduct()) {
            return; // Successfully restored to product
          }
          
          // If product not found and we have infinite scroll, try to trigger loading
          if (savedProductId) {
            const customPagination = document.querySelector('custom-pagination');
            const loadMoreButton = customPagination ? customPagination.querySelector('.js-pagination-load-more') : null;
            
            // If load more button exists and product not found, it might be on next page
            // Scroll to approximate position first, then infinite scroll will load more
            if (loadMoreButton && !document.querySelector(`.js-pagination-result[data-product-id="${savedProductId}"]`)) {
              // Use saved scroll position to get close, then infinite scroll will load the product
              const pageHeight = Math.max(
                document.body.scrollHeight,
                document.body.offsetHeight,
                document.documentElement.clientHeight,
                document.documentElement.scrollHeight,
                document.documentElement.offsetHeight
              );
              const maxScroll = pageHeight - window.innerHeight;
              
              if (scrollY > 0 && scrollY < maxScroll - 200) {
                window.scrollTo({
                  top: scrollY,
                  behavior: 'auto'
                });
                document.documentElement.scrollTop = scrollY;
                document.body.scrollTop = scrollY;
              }
              
              // Infinite scroll will automatically load more when we're near the bottom
              // The swym:collections-loaded event handler will then scroll to the exact product
              return;
            }
          }
          
          // Fallback: If product not found, try using saved scroll position
          const pageHeight = Math.max(
            document.body.scrollHeight,
            document.body.offsetHeight,
            document.documentElement.clientHeight,
            document.documentElement.scrollHeight,
            document.documentElement.offsetHeight
          );
          const maxScroll = pageHeight - window.innerHeight;
          
          // Only restore if saved position is reasonable (not at footer, at least 200px from bottom)
          if (scrollY > 0 && scrollY < maxScroll - 200) {
            window.scrollTo({
              top: scrollY,
              behavior: 'auto'
            });
            document.documentElement.scrollTop = scrollY;
            document.body.scrollTop = scrollY;
          }
        };
        
        // Try multiple times with increasing delays to handle dynamic content loading (infinite scroll)
        const attempts = [100, 300, 500, 800, 1200, 2000];
        attempts.forEach((delay) => {
          setTimeout(restoreAfterLoad, delay);
        });
      }
    }
  }

  // Restore scroll position when returning via browser back button
  window.addEventListener('pageshow', (e) => {
    // pageshow event fires on both initial load and back/forward navigation
    // e.persisted is true when page is loaded from cache (back/forward)
    if (e.persisted) {
      restoreScrollPosition();
    }
  });

  // Also restore on DOMContentLoaded as fallback
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', restoreScrollPosition);
  } else {
    // DOM already loaded, restore immediately
    restoreScrollPosition();
  }

  // Additional fallback after window load
  window.addEventListener('load', () => {
    setTimeout(restoreScrollPosition, 50);
  });
  
  // Listen for infinite scroll loads and try to restore after each load
  let infiniteScrollRestoreAttempts = 0;
  const maxInfiniteScrollAttempts = 10; // Limit attempts to prevent infinite loop
  
  document.addEventListener('swym:collections-loaded', () => {
    // After infinite scroll loads more products, try to restore position
    infiniteScrollRestoreAttempts++;
    if (infiniteScrollRestoreAttempts > maxInfiniteScrollAttempts) {
      return; // Stop trying after max attempts
    }
    
    setTimeout(() => {
      const savedProductId = sessionStorage.getItem('collectionProductId');
      const savedUrl = sessionStorage.getItem('collectionUrl');
      
      if (savedProductId && savedUrl) {
        // Check if URLs match
        const currentUrl = window.location.href.split('?')[0];
        const savedUrlBase = savedUrl.split('?')[0];
        
        if (currentUrl === savedUrlBase) {
          const productElement = document.querySelector(`.js-pagination-result[data-product-id="${savedProductId}"]`);
          if (productElement) {
            // Product found after infinite scroll load, scroll to it precisely
            const rect = productElement.getBoundingClientRect();
            const currentScroll = window.pageYOffset || document.documentElement.scrollTop;
            
            // Calculate exact scroll position to center the product with offset from top
            const targetScroll = currentScroll + rect.top - 120; // 120px offset from top for header
            
            window.scrollTo({
              top: Math.max(0, targetScroll),
              behavior: 'auto'
            });
            document.documentElement.scrollTop = Math.max(0, targetScroll);
            document.body.scrollTop = Math.max(0, targetScroll);
            
            // Reset attempts counter on success
            infiniteScrollRestoreAttempts = 0;
          }
        }
      }
    }, 200);
  });
}

/**
 * Accessibility toolbar (e.g. #enable-toolbar-trigger) often lives inside a fixed parent with a huge
 * z-index, so CSS on the trigger alone cannot place it below the mobile drawer (z-index 30).
 */
(function accessibilityToolbarBelowMobileNav() {
  const MENU_OPEN_CLASS = 'mobile-menu-open';
  const NAV_Z_INDEX = '25';
  const MAX_ANCESTORS = 16;

  function parseZIndex(value) {
    if (value == null || value === 'auto') return NaN;
    const n = parseInt(value, 10);
    return Number.isNaN(n) ? NaN : n;
  }

  function shouldLowerStacking(elStyle) {
    const pos = elStyle.position;
    const z = parseZIndex(elStyle.zIndex);
    return pos === 'fixed' || pos === 'sticky' || (!Number.isNaN(z) && z > 30);
  }

  function applyStacking(belowNav) {
    const trigger = document.getElementById('enable-toolbar-trigger');
    if (!trigger || !document.body) return;

    let el = trigger;
    let depth = 0;
    if (belowNav) {
      while (el && el !== document.body && depth < MAX_ANCESTORS) {
        const cs = window.getComputedStyle(el);
        if (shouldLowerStacking(cs)) {
          if (!('ccA11yPrevZ' in el.dataset)) {
            el.dataset.ccA11yPrevZ = el.style.zIndex || '';
            el.dataset.ccA11yPrevZPr = el.style.getPropertyPriority('z-index') || '';
          }
          el.style.setProperty('z-index', NAV_Z_INDEX, 'important');
        }
        el = el.parentElement;
        depth += 1;
      }
    } else {
      while (el && el !== document.body && depth < MAX_ANCESTORS) {
        if ('ccA11yPrevZ' in el.dataset) {
          const prev = el.dataset.ccA11yPrevZ;
          const pr = el.dataset.ccA11yPrevZPr;
          if (prev === '') {
            el.style.removeProperty('z-index');
          } else {
            el.style.setProperty('z-index', prev, pr || undefined);
          }
          delete el.dataset.ccA11yPrevZ;
          delete el.dataset.ccA11yPrevZPr;
        }
        el = el.parentElement;
        depth += 1;
      }
    }
  }

  function scheduleApply() {
    const open = document.body.classList.contains(MENU_OPEN_CLASS);
    applyStacking(open);
    requestAnimationFrame(() => applyStacking(document.body.classList.contains(MENU_OPEN_CLASS)));
    setTimeout(() => applyStacking(document.body.classList.contains(MENU_OPEN_CLASS)), 120);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleApply);
  } else {
    scheduleApply();
  }

  new MutationObserver(scheduleApply).observe(document.body, {
    attributes: true,
    attributeFilter: ['class']
  });
})();

/**
 * Third-party app injects `.div_slider_container .featured-collection-slider { … min-width: auto !important; }`
 * after theme CSS. Cascade + specificity can still lose; inline `important` reliably overrides author stylesheets.
 *
 * Keep slider chips centered across breakpoints.
 */
(function enforceCollectionSubcategorySliderFlexSizing() {
  const SELECTOR = '.div_slider_container ul.featured-collection-slider';

  function apply() {
    document.querySelectorAll(SELECTOR).forEach((ul) => {
      if (ul.classList.contains('center-on-mobile')) return;
      ul.style.setProperty('min-width', 'max-content', 'important');
      ul.style.setProperty('justify-content', 'center', 'important');
      ul.style.setProperty('width', 'auto', 'important');
    });
  }

  function runSoon() {
    apply();
    requestAnimationFrame(apply);
    setTimeout(apply, 50);
    setTimeout(apply, 300);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runSoon);
  } else {
    runSoon();
  }
  window.addEventListener('load', runSoon);
  document.addEventListener('shopify:section:load', runSoon);

  let debounce;
  if (typeof MutationObserver !== 'undefined') {
    new MutationObserver(() => {
      if (!document.querySelector(SELECTOR)) return;
      clearTimeout(debounce);
      debounce = setTimeout(apply, 80);
    }).observe(document.body, { childList: true, subtree: true });
  }
})();

/**
 * Editor-notes accordion title is authored in the metafield.
 * Visually's mobile PDP experiment replaces .acc_title text; this span is
 * outside that selector, and any later text rewrite is reverted.
 */
(function lockEditorNotesTitle() {
  const HEBREW = /[\u0590-\u05FF\u05F0-\u05F4\uFB1D-\uFB4F]/;

  function apply(el) {
    const title = el.getAttribute('data-title');
    if (title == null) return;
    if (el.textContent !== title) el.textContent = title;

    const hebrew = HEBREW.test(title);
    el.classList.toggle('english', !hebrew);
    el.classList.toggle('langhe', hebrew);
  }

  function run() {
    document.querySelectorAll('.editor-notes-title[data-vsly-lock]').forEach((el) => {
      apply(el);
      if (el.dataset.vslyLockBound === '1') return;
      el.dataset.vslyLockBound = '1';
      if (typeof MutationObserver === 'undefined') return;
      new MutationObserver(() => apply(el)).observe(el, {
        childList: true,
        characterData: true,
        subtree: true
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
  window.addEventListener('load', run);
  document.addEventListener('shopify:section:load', run);
})();
